import React from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  Platform,
  Modal,
} from "react-native";
import LottieView from "lottie-react-native";

type WidgetDeployCelebrationModalProps = {
  visible: boolean;
  onClose: () => void;
  onShown?: () => void;
  deployedHost?: string | null;
};

const WidgetDeployCelebrationModal: React.FC<
  WidgetDeployCelebrationModalProps
> = ({ visible, onClose, onShown, deployedHost }) => {
  const webLottieStyle = React.useMemo(
    () => ({ width: "140px", height: "90px" }),
    [],
  );
  const hasFiredShownRef = React.useRef(false);

  React.useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      onClose();
    }, 6500);

    return () => {
      clearTimeout(timer);
    };
  }, [onClose, visible]);

  React.useEffect(() => {
    if (!visible) {
      hasFiredShownRef.current = false;
      return;
    }
    if (hasFiredShownRef.current) return;
    hasFiredShownRef.current = true;
    onShown?.();
  }, [onShown, visible]);

  if (!visible) return null;

  const content = (
    <View style={styles.overlay} pointerEvents="box-none">
      <View style={styles.container}>
        <Pressable
          onPress={onClose}
          style={styles.closeButton}
          accessibilityRole="button"
          accessibilityLabel="Dismiss celebration"
        >
          <Text style={styles.closeButtonText}>×</Text>
        </Pressable>

        <View style={styles.lottieContainer}>
          <LottieView
            autoPlay
            loop={false}
            source={require("../../../assets/animations/celebration_3f0bb824-117d-11ee-b120-9b95c3abdf64.json")}
            style={styles.lottie}
            webStyle={webLottieStyle}
          />
        </View>

        <Text style={styles.titleText}>Widget successfully deployed</Text>
        <Text style={styles.messageText}>
          {deployedHost
            ? `Detected live on ${deployedHost}.`
            : "Detected live on your website."}
        </Text>
        {Platform.OS === "web" ? (
          <Text style={styles.helperText}>
            New leads from your site will now appear in your dashboard.
          </Text>
        ) : null}
      </View>
    </View>
  );

  if (Platform.OS === "web") {
    return <View style={styles.webRoot}>{content}</View>;
  }

  return (
    <Modal animationType="fade" transparent visible onRequestClose={onClose}>
      {content}
    </Modal>
  );
};

const styles = StyleSheet.create({
  webRoot: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 9999,
    pointerEvents: "box-none",
  },
  overlay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    padding: 16,
  },
  container: {
    width: "100%",
    maxWidth: 360,
    backgroundColor: "#FFFFFF",
    borderColor: "#DDDEE0",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 14,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 6,
  },
  closeButton: {
    alignSelf: "flex-end",
    minWidth: 32,
    minHeight: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonText: {
    fontSize: 24,
    lineHeight: 24,
    color: "#6E727F",
    fontFamily: "Urbanist600",
  },
  lottieContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: -10,
    marginBottom: 4,
  },
  lottie: {
    width: 140,
    height: 90,
  },
  titleText: {
    fontFamily: "Urbanist700",
    color: "#1F2330",
    fontSize: 18,
    lineHeight: 24,
    textAlign: "center",
    marginBottom: 4,
  },
  messageText: {
    fontFamily: "Urbanist500",
    color: "#282B33",
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
  },
  helperText: {
    marginTop: 6,
    fontFamily: "Urbanist500",
    color: "#5D6170",
    fontSize: 12,
    lineHeight: 16,
    textAlign: "center",
  },
});

export default WidgetDeployCelebrationModal;
